import React, { useRef, useState, useEffect } from 'react';
import './WalkthroughVideoModal.css';

/**
 * WalkthroughPickerModal
 *
 * Two-state modal for multi-video walkthrough sections.
 *
 * List state  → shows video cards (title + description + Watch button)
 * Playing state → shows the selected video inline with a ← Back button
 *
 * Props:
 *   isOpen   boolean
 *   onClose  () => void
 *   videos   TutorialVideo[]   — all active videos for this section
 */
export default function WalkthroughPickerModal({ isOpen, onClose, videos }) {
  const [playing, setPlaying] = useState(null); // null = list, or a video object
  const iframeRef = useRef(null);

  // Stop video and reset to list when modal closes
  useEffect(() => {
    if (!isOpen) {
      if (iframeRef.current) iframeRef.current.src = '';
      setPlaying(null);
    }
  }, [isOpen]);

  function handleWatch(video) {
    setPlaying(video);
  }

  function handleBack() {
    if (iframeRef.current) iframeRef.current.src = '';
    setPlaying(null);
  }

  function handleClose() {
    if (iframeRef.current) iframeRef.current.src = '';
    setPlaying(null);
    onClose();
  }

  if (!isOpen) return null;

  return (
    <div className="walkthrough-overlay" onClick={handleClose}>
      <div
        className={`walkthrough-card ${playing ? '' : 'walkthrough-picker-narrow'}`}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="walkthrough-header">
          {playing ? (
            <button className="walkthrough-back-btn" onClick={handleBack}>
              ← Back
            </button>
          ) : (
            <h3 className="walkthrough-title">Walkthrough Videos</h3>
          )}
          <button className="walkthrough-close" onClick={handleClose} aria-label="Close">
            ✕
          </button>
        </div>

        {/* Playing state — video iframe */}
        {playing && (
          <>
            <p className="walkthrough-playing-title">{playing.title}</p>
            <div className="walkthrough-video-wrapper">
              <iframe
                ref={iframeRef}
                className="walkthrough-iframe"
                src={playing.embed_url}
                title={playing.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                allowFullScreen
              />
            </div>
          </>
        )}

        {/* List state — video cards */}
        {!playing && (
          <div className="walkthrough-picker-list">
            {videos.map(video => (
              <div key={video.id} className="walkthrough-picker-card">
                <div className="walkthrough-picker-card-text">
                  <span className="walkthrough-picker-card-title">{video.title}</span>
                  {video.description && (
                    <span className="walkthrough-picker-card-desc">{video.description}</span>
                  )}
                </div>
                <button
                  className="walkthrough-picker-watch-btn"
                  onClick={() => handleWatch(video)}
                >
                  <svg width="9" height="9" viewBox="0 0 10 10" fill="currentColor">
                    <polygon points="1,0 10,5 1,10" />
                  </svg>
                  Watch
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
